import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Briefcase, Clock, MapPin } from 'lucide-react';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const UserDashboard = () => {
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const data = await api.applications.getMy();
      setApplications(data);
    } catch (error) {
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500/10 text-yellow-500';
      case 'reviewed':
        return 'bg-blue-500/10 text-blue-500';
      case 'accepted':
        return 'bg-green-500/10 text-green-500';
      case 'rejected':
        return 'bg-red-500/10 text-red-500';
      default:
        return 'bg-slate-500/10 text-slate-500';
    }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />

      <div className="py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <h1 className="text-3xl md:text-5xl font-semibold tracking-tight mb-4" data-testid="dashboard-heading">
              Welcome, <span className="gradient-text">{user.name}</span>
            </h1>
            <p className="text-lg text-slate-400">Track your job applications and career progress</p>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-semibold mb-6">My Applications</h2>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
              </div>
            ) : applications.length === 0 ? (
              <div className="glass-card rounded-2xl p-12 text-center" data-testid="no-applications">
                <Briefcase className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 text-lg mb-6">You haven't applied to any jobs yet</p>
                <Link to="/jobs">
                  <button className="btn-primary">Browse Jobs</button>
                </Link>
              </div>
            ) : (
              <div className="grid gap-6" data-testid="applications-list">
                {applications.map((app, index) => (
                  <motion.div
                    key={app.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="glass-card rounded-2xl p-6"
                    data-testid={`application-${app.id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-semibold">Job ID: {app.job_id.slice(0, 8)}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-mono ${getStatusColor(app.status)}`}>
                            {app.status}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-400">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            Applied {new Date(app.applied_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;